import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { formatDistanceToNow } from "date-fns";
import { ImagePlus, Loader2, Lock, LogOut, Megaphone, Trash2, UploadCloud } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "jgw_admin_token";

const mediaUrl = (item) => `${process.env.REACT_APP_BACKEND_URL}/api/media/${item.media_path}`;
const timeAgo = (iso) => {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return "";
  }
};

const fieldClass =
  "h-auto w-full rounded-xl border-white/15 bg-white/[0.06] px-4 py-3 text-[15px] text-white placeholder:text-slate-500 focus-visible:ring-1 focus-visible:ring-whatsapp/60 focus-visible:border-whatsapp/60";

const SLOT_LABELS = {
  banner: "Banner — wide spot below the top story",
  feed: "In-feed — a card among the news",
};

const FileDrop = ({ file, setFile, inputRef, testId }) => (
  <>
    <label
      htmlFor={testId}
      data-testid={`${testId}-dropzone`}
      className="mt-4 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-white/15 bg-black/30 px-4 py-7 text-center transition-colors duration-200 hover:border-whatsapp/50"
    >
      {file ? (
        <>
          <UploadCloud size={24} className="text-whatsapp" />
          <span className="text-sm font-semibold text-white">{file.name}</span>
          <span className="text-xs text-silver-dark">Tap to choose a different file</span>
        </>
      ) : (
        <>
          <ImagePlus size={24} className="text-silver" />
          <span className="text-sm font-semibold text-white">Choose a photo or video</span>
          <span className="text-xs text-silver-dark">From your gallery or files</span>
        </>
      )}
    </label>
    <input
      id={testId}
      ref={inputRef}
      type="file"
      accept="image/*,video/*"
      data-testid={testId}
      onChange={(e) => setFile(e.target.files?.[0] || null)}
      className="hidden"
    />
  </>
);

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const [posts, setPosts] = useState([]);
  const [ads, setAds] = useState({});

  const [file, setFile] = useState(null);
  const [description, setDescription] = useState("");
  const [link, setLink] = useState("");
  const [publishing, setPublishing] = useState(false);
  const fileInputRef = useRef(null);

  const [adFile, setAdFile] = useState(null);
  const [adSlot, setAdSlot] = useState("banner");
  const [adDescription, setAdDescription] = useState("");
  const [adLink, setAdLink] = useState("");
  const [adPublishing, setAdPublishing] = useState(false);
  const adFileInputRef = useRef(null);

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadAll = useCallback(async () => {
    try {
      const [{ data: postsData }, { data: adsData }] = await Promise.all([
        axios.get(`${API}/posts`),
        axios.get(`${API}/ads`),
      ]);
      setPosts(postsData);
      setAds(adsData || {});
    } catch {
      /* keep current data */
    }
  }, []);

  useEffect(() => {
    if (token) loadAll();
  }, [token, loadAll]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoginError(typeof detail === "string" ? detail : "Login failed. Try again.");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleAuthError = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  const handlePublish = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please choose a photo or video first.");
      return;
    }
    setPublishing(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("description", description);
      form.append("link", link);
      const { data } = await axios.post(`${API}/admin/posts`, form, { headers: authHeaders() });
      setPosts((p) => [data, ...p]);
      setFile(null);
      setDescription("");
      setLink("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.success("Update published.");
    } catch (err) {
      if (err.response?.status === 401) return handleAuthError();
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Publishing failed. Try again.");
    } finally {
      setPublishing(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this update?")) return;
    try {
      await axios.delete(`${API}/admin/posts/${id}`, { headers: authHeaders() });
      setPosts((p) => p.filter((post) => post.id !== id));
      toast.success("Update deleted.");
    } catch (err) {
      if (err.response?.status === 401) return handleAuthError();
      toast.error("Delete failed. Try again.");
    }
  };

  const handlePublishAd = async (e) => {
    e.preventDefault();
    if (!adFile) {
      toast.error("Please choose a photo or video for the advertisement.");
      return;
    }
    setAdPublishing(true);
    try {
      const form = new FormData();
      form.append("file", adFile);
      form.append("slot", adSlot);
      form.append("description", adDescription);
      form.append("link", adLink);
      await axios.post(`${API}/admin/ads`, form, { headers: authHeaders() });
      await loadAll();
      setAdFile(null);
      setAdDescription("");
      setAdLink("");
      if (adFileInputRef.current) adFileInputRef.current.value = "";
      toast.success("Advertisement published.");
    } catch (err) {
      if (err.response?.status === 401) return handleAuthError();
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Publishing failed. Try again.");
    } finally {
      setAdPublishing(false);
    }
  };

  const handleDeleteAd = async (id) => {
    if (!window.confirm("Remove this advertisement?")) return;
    try {
      await axios.delete(`${API}/admin/ads/${id}`, { headers: authHeaders() });
      setAds((prev) => {
        const next = { ...prev };
        for (const slot of Object.keys(next)) {
          if (next[slot]?.id === id) delete next[slot];
        }
        return next;
      });
      toast.success("Advertisement removed.");
    } catch (err) {
      if (err.response?.status === 401) return handleAuthError();
      toast.error("Remove failed. Try again.");
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink px-5">
        <form
          onSubmit={handleLogin}
          data-testid="admin-login-form"
          className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/[0.04] p-8"
        >
          <img src="/jgw-logo.jpeg" alt="JGW" className="mx-auto h-14 w-14 rounded-full ring-1 ring-white/20" />
          <h1 className="mt-5 text-center font-display text-2xl font-bold text-white">JGW Admin</h1>
          <p className="mt-2 text-center text-sm text-silver">Enter the admin password to post updates.</p>
          <div className="relative mt-6">
            <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-silver-dark" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              data-testid="admin-password-input"
              className={`${fieldClass} pl-11`}
              required
            />
          </div>
          {loginError && (
            <p data-testid="admin-login-error" className="mt-3 text-sm font-semibold text-red-400">
              {loginError}
            </p>
          )}
          <button
            type="submit"
            disabled={loggingIn}
            data-testid="admin-login-button"
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-whatsapp py-3.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-whatsapp-dark disabled:opacity-60"
          >
            {loggingIn && <Loader2 size={16} className="animate-spin" />}
            Log In
          </button>
          <a href="/" className="mt-5 block text-center text-xs font-semibold text-silver-dark hover:text-white">
            ← Back to website
          </a>
        </form>
      </div>
    );
  }

  const activeAds = Object.entries(ads);

  return (
    <div className="min-h-screen bg-ink pb-16">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-black/92 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-5">
          <div className="flex items-center gap-3">
            <img src="/jgw-logo.jpeg" alt="JGW" className="h-9 w-9 rounded-full ring-1 ring-white/20" />
            <span className="font-display text-sm font-bold text-white">JGW Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-xs font-semibold text-silver-dark hover:text-white">
              View site
            </a>
            <button
              type="button"
              onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(null); }}
              data-testid="admin-logout-button"
              className="flex items-center gap-1.5 text-xs font-semibold text-silver hover:text-white"
            >
              <LogOut size={14} />
              Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 pt-8">
        <form
          onSubmit={handlePublish}
          data-testid="admin-post-form"
          className="rounded-2xl border border-white/10 bg-white/[0.04] p-6"
        >
          <h2 className="font-display text-lg font-bold text-white">Post a new update</h2>
          <FileDrop file={file} setFile={setFile} inputRef={fileInputRef} testId="admin-file-input" />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Update text / description"
            required
            rows={4}
            data-testid="admin-description-input"
            className="mt-4 w-full rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3 text-[15px] text-white placeholder:text-slate-500 focus:border-whatsapp/60 focus:outline-none"
          />
          <input
            type="url"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="Link (optional, https://…)"
            data-testid="admin-link-input"
            className="mt-4 w-full rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3 text-[15px] text-white placeholder:text-slate-500 focus:border-whatsapp/60 focus:outline-none"
          />
          <button
            type="submit"
            disabled={publishing}
            data-testid="admin-publish-button"
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-whatsapp py-4 text-sm font-bold text-white transition-colors duration-200 hover:bg-whatsapp-dark disabled:opacity-60"
          >
            {publishing ? (
              <>
                <Loader2 size={17} className="animate-spin" />
                Publishing…
              </>
            ) : (
              "Publish Update"
            )}
          </button>
          <p className="mt-3 text-center text-xs text-silver-dark">
            Updates are automatically removed after 7 days.
          </p>
        </form>

        <form
          onSubmit={handlePublishAd}
          data-testid="admin-ad-form"
          className="mt-8 rounded-2xl border border-white/10 bg-white/[0.04] p-6"
        >
          <h2 className="flex items-center gap-2.5 font-display text-lg font-bold text-white">
            <Megaphone size={18} className="text-whatsapp" />
            Post an advertisement
          </h2>
          <p className="mt-2 text-xs leading-relaxed text-silver-dark">
            Advertisements stay live until you remove them. When a spot has no
            advertisement, it automatically shows news instead.
          </p>

          <label className="mt-5 block text-xs font-bold uppercase tracking-[0.15em] text-silver">
            Ad spot
          </label>
          <select
            value={adSlot}
            onChange={(e) => setAdSlot(e.target.value)}
            data-testid="admin-ad-slot-select"
            className="mt-2 w-full rounded-xl border border-white/15 bg-charcoal-800 px-4 py-3 text-[15px] text-white focus:border-whatsapp/60 focus:outline-none"
          >
            {Object.entries(SLOT_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>

          <FileDrop file={adFile} setFile={setAdFile} inputRef={adFileInputRef} testId="admin-ad-file-input" />

          <textarea
            value={adDescription}
            onChange={(e) => setAdDescription(e.target.value)}
            placeholder="Ad text (optional)"
            rows={3}
            data-testid="admin-ad-description-input"
            className="mt-4 w-full rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3 text-[15px] text-white placeholder:text-slate-500 focus:border-whatsapp/60 focus:outline-none"
          />
          <input
            type="url"
            value={adLink}
            onChange={(e) => setAdLink(e.target.value)}
            placeholder="Link (optional, https://…)"
            data-testid="admin-ad-link-input"
            className="mt-4 w-full rounded-xl border border-white/15 bg-white/[0.06] px-4 py-3 text-[15px] text-white placeholder:text-slate-500 focus:border-whatsapp/60 focus:outline-none"
          />
          <button
            type="submit"
            disabled={adPublishing}
            data-testid="admin-ad-publish-button"
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-white py-4 text-sm font-bold text-black transition-colors duration-200 hover:bg-whatsapp disabled:opacity-60"
          >
            {adPublishing ? (
              <>
                <Loader2 size={17} className="animate-spin" />
                Publishing…
              </>
            ) : (
              "Publish Advertisement"
            )}
          </button>
        </form>

        {activeAds.length > 0 && (
          <>
            <h3 className="mt-10 font-display text-base font-bold text-white">
              Live advertisements <span className="text-silver-dark">({activeAds.length})</span>
            </h3>
            <div className="mt-4 space-y-3">
              {activeAds.map(([slot, ad]) => (
                <div
                  key={ad.id}
                  data-testid={`admin-ad-${ad.id}`}
                  className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.04] p-3.5"
                >
                  {ad.media_type === "video" ? (
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-black/50 text-whatsapp">
                      <UploadCloud size={18} />
                    </div>
                  ) : (
                    <img src={mediaUrl(ad)} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" loading="lazy" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-white">
                      {ad.description || "Advertisement"}
                    </p>
                    <p className="mt-0.5 text-xs text-silver-dark">
                      {SLOT_LABELS[slot]} • {timeAgo(ad.created_at)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDeleteAd(ad.id)}
                    data-testid={`admin-ad-delete-${ad.id}`}
                    aria-label="Remove advertisement"
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 text-silver transition-colors duration-200 hover:border-red-500/50 hover:text-red-400"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        <h3 className="mt-10 font-display text-base font-bold text-white">
          Recent updates <span className="text-silver-dark">({posts.length})</span>
        </h3>
        <div className="mt-4 space-y-3">
          {posts.map((post) => (
            <div
              key={post.id}
              data-testid={`admin-post-${post.id}`}
              className="flex items-center gap-4 rounded-xl border border-white/10 bg-white/[0.04] p-3.5"
            >
              {post.media_type === "video" ? (
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-black/50 text-whatsapp">
                  <UploadCloud size={18} />
                </div>
              ) : (
                <img
                  src={mediaUrl(post)}
                  alt=""
                  className="h-14 w-14 shrink-0 rounded-lg object-cover"
                  loading="lazy"
                />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-white">{post.description}</p>
                <p className="mt-0.5 text-xs text-silver-dark">
                  {timeAgo(post.created_at)} • {post.media_type}
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleDelete(post.id)}
                data-testid={`admin-delete-${post.id}`}
                aria-label="Delete update"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-white/10 text-silver transition-colors duration-200 hover:border-red-500/50 hover:text-red-400"
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
          {posts.length === 0 && (
            <p className="rounded-xl border border-white/10 bg-white/[0.04] p-6 text-center text-sm text-silver">
              No updates yet. Publish your first one above.
            </p>
          )}
        </div>
      </main>
    </div>
  );
}
